/** Tabs component - tab navigation UI. */

"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

type TabsListVariant = "default" | "segmented";
type TabsListSize = "default" | "sm";

/**
 * Tabs context.
 */
interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  listVariant: TabsListVariant;
  size: TabsListSize;
}

const TabsContext = React.createContext<TabsContextValue | undefined>(undefined);

const tabsListVariants = cva("inline-flex items-center justify-center gap-1 text-muted-foreground", {
  variants: {
    variant: {
      default: "surface-panel h-10 rounded-md border p-1",
      segmented: "surface-panel rounded-lg border p-1",
    },
    size: {
      default: "h-10",
      sm: "h-8",
    },
    fullWidth: {
      true: "w-full",
      false: "w-auto",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    fullWidth: false,
  },
});

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "",
        segmented: "",
      },
      size: {
        default: "px-3 py-1.5 text-sm",
        sm: "px-2.5 py-1 text-xs",
      },
      active: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        active: true,
        className: "surface-control text-foreground",
      },
      {
        variant: "default",
        active: false,
        className: "text-muted-foreground hover:bg-background/50 hover:text-foreground",
      },
      {
        variant: "segmented",
        active: true,
        className: "surface-control text-foreground ring-1 ring-border",
      },
      {
        variant: "segmented",
        active: false,
        className: "text-muted-foreground hover:bg-background/70 hover:text-foreground",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      active: false,
    },
  },
);

/**
 * Tabs props.
 */
export interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
  listVariant?: TabsListVariant;
  size?: TabsListSize;
}

/**
 * Tabs component - container for tab navigation.
 *
 * @param props - Tabs props
 * @returns Tabs component
 */
export function Tabs({ defaultValue, value: controlledValue, onValueChange, children, className, listVariant = "default", size = "default" }: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, onValueChange],
  );

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleValueChange, listVariant, size }}>
      <div className={cn("w-full", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

/**
 * TabsList props.
 */
export interface TabsListProps extends VariantProps<typeof tabsListVariants> {
  children: React.ReactNode;
  className?: string;
}

/**
 * TabsList component - container for tab triggers.
 *
 * @param props - TabsList props
 * @returns TabsList component
 */
export function TabsList({ children, className, variant, size, fullWidth }: TabsListProps) {
  const context = React.useContext(TabsContext);
  return <div className={cn(tabsListVariants({ variant: variant ?? context?.listVariant, size: size ?? context?.size, fullWidth }), className)}>{children}</div>;
}

/**
 * TabsTrigger props.
 */
export interface TabsTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type" | "value" | "onClick" | "children">,
    VariantProps<typeof tabsTriggerVariants> {
  value: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * TabsTrigger component - individual tab button.
 *
 * @param props - TabsTrigger props
 * @returns TabsTrigger component
 */
export function TabsTrigger({ value, children, className, variant, size, ...rest }: TabsTriggerProps) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error("TabsTrigger must be used within Tabs");
  }

  const isActive = context.value === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-state={isActive ? "active" : "inactive"}
      onClick={() => context.onValueChange(value)}
      className={cn(
        tabsTriggerVariants({
          variant: variant ?? context.listVariant,
          size: size ?? context.size,
          active: isActive,
        }),
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * TabsContent props.
 */
export interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * TabsContent component - content panel for a tab.
 *
 * @param props - TabsContent props
 * @returns TabsContent component
 */
export function TabsContent({ value, children, className }: TabsContentProps) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error("TabsContent must be used within Tabs");
  }

  if (context.value !== value) {
    return null;
  }

  return <div className={cn("mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className)}>{children}</div>;
}

export { tabsListVariants, tabsTriggerVariants };
